import { AlertBanner, Button, Flex } from '@brightdrop/bd-ui';
import { Card, CardContent, FormControlLabel, Radio } from '@mui/material';
import clsx from 'clsx';
import type { ComponentType } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';
import { EnrollOptions } from '~/common/models/catalog.model';
import CatalogRadioGroup from '~/features/catalog/shared/components/catalogRadioGroup/CatalogRadioGroup';
import PartnerLogo from '~/features/integrations/partners/PartnerLogo';
import type {
  PartnerId,
  WizardStepProps,
} from '~/features/integrations/partners/types';
import WizardSection from '~/features/integrations/wizard/WizardSection';

import useStyles from './VehicleSelectionStep.styles';

export type VehicleSelectionTranslationKeys = {
  sectionTitle: string;
  allOption: string;
  individualOption: string;
};

export type VehicleSelectionStepProps = WizardStepProps & {
  eligibleCount: number;
  /** Pre-selected when switching to individual selection; individual stays disabled until loaded. */
  selectedVins?: string[];
  isEligibilityLoading: boolean;
  isEligibilityError: boolean;
  partnerId: PartnerId;
  /** Prefixes every testid: `${testIdPrefix}-section`, `-enroll-all`, `-enroll-individual`. */
  testIdPrefix: string;
  translationKeys: VehicleSelectionTranslationKeys;
  ViewVehiclesSubView: ComponentType<WizardStepProps>;
  SelectVehiclesSubView: ComponentType<WizardStepProps>;
};

/** A partner's second wizard step: enroll all eligible vehicles, or pick them individually. */
function VehicleSelectionStep({
  draft,
  onChange,
  onValidityChange,
  onOpenSubView,
  eligibleCount,
  selectedVins,
  isEligibilityLoading,
  isEligibilityError,
  partnerId,
  testIdPrefix,
  translationKeys,
  ViewVehiclesSubView,
  SelectVehiclesSubView,
  allVehiclesOnly,
}: VehicleSelectionStepProps) {
  const { t } = useTranslation(['integrations', 'common', 'catalog']);
  const { classes } = useStyles();

  const selectedVehicleIds = draft.vehicleSelection?.vehicleIds ?? [];
  // Read from the draft so it survives this step remounting after a sub-view closes.
  const enrollOption = draft.enrollOption ?? EnrollOptions.ALL;
  const isIndividualDisabled =
    isEligibilityLoading ||
    isEligibilityError ||
    !selectedVins ||
    eligibleCount < 2;

  // Lets a card click move keyboard focus onto its own radio, same as a direct radio click would.
  const optionRadioRefs: Record<
    EnrollOptions,
    React.RefObject<HTMLInputElement>
  > = {
    [EnrollOptions.ALL]: useRef(null),
    [EnrollOptions.INDIVIDUAL]: useRef(null),
  };

  // Enrolling all needs at least one eligible vehicle; enrolling individually needs at least one picked.
  useEffect(() => {
    onValidityChange(
      !isEligibilityLoading &&
        !isEligibilityError &&
        (enrollOption === EnrollOptions.ALL
          ? eligibleCount > 0
          : selectedVehicleIds.length > 0)
    );
  }, [
    enrollOption,
    eligibleCount,
    selectedVehicleIds.length,
    isEligibilityLoading,
    isEligibilityError,
    onValidityChange,
  ]);

  useEffect(() => {
    if (isEligibilityLoading || isEligibilityError || draft.vehicleSelection) {
      return;
    }
    onChange({
      vehicleSelection: {
        fleetIds: [],
        vehicleIds: [],
      },
    });
  }, [
    draft.vehicleSelection,
    isEligibilityLoading,
    isEligibilityError,
    onChange,
  ]);

  const handleOptionChange = (option: EnrollOptions) => {
    if (option === EnrollOptions.INDIVIDUAL && isIndividualDisabled) return;
    if (option === enrollOption) return;
    onChange({
      enrollOption: option,
      vehicleSelection: {
        fleetIds: [],
        vehicleIds:
          option === EnrollOptions.INDIVIDUAL ? (selectedVins ?? []) : [],
      },
    });
  };

  const openViewVehicles = () =>
    onOpenSubView({
      titleKey: 'catalog:eligibleVehicles.title',
      primaryLabelKey: 'common:enroll',
      component: ViewVehiclesSubView,
    });

  const openSelectVehicles = () =>
    onOpenSubView({
      titleKey: 'catalog:eligibleVehicles.title',
      primaryLabelKey: 'common:enroll',
      component: SelectVehiclesSubView,
    });

  const options = [
    {
      key: EnrollOptions.ALL,
      id: `${testIdPrefix}-enroll-all`,
      labelKey: translationKeys.allOption,
      count: eligibleCount,
      disabled: false,
      showButton: enrollOption === EnrollOptions.ALL,
      buttonLabelKey: 'catalog:buttons.viewVehicles_plural',
      onButtonClick: openViewVehicles,
    },
    {
      key: EnrollOptions.INDIVIDUAL,
      id: `${testIdPrefix}-enroll-individual`,
      labelKey: translationKeys.individualOption,
      count: selectedVehicleIds.length,
      disabled: isIndividualDisabled,
      showButton: enrollOption === EnrollOptions.INDIVIDUAL,
      buttonLabelKey: 'catalog:order.selectVehicles',
      onButtonClick: openSelectVehicles,
    },
  ].filter(
    (option) => !(allVehiclesOnly && option.key === EnrollOptions.INDIVIDUAL)
  );

  return (
    <WizardSection
      number={2}
      title={t(translationKeys.sectionTitle)}
      trailing={<PartnerLogo partnerId={partnerId} variant="icon" />}
      testId={`${testIdPrefix}-section`}
    >
      {isEligibilityError && (
        <AlertBanner
          variant="error"
          open
          title={t('common:errors.unableToLoad')}
          testId={`${testIdPrefix}-load-error`}
        />
      )}
      <CatalogRadioGroup
        aria-label={t(translationKeys.sectionTitle)}
        name={`${testIdPrefix}-enroll-options`}
        value={enrollOption}
        onChange={(e) => handleOptionChange(e.target.value as EnrollOptions)}
      >
        {options.map((option) => (
          <Card
            key={option.key}
            data-testid={option.id}
            aria-disabled={option.disabled}
            className={clsx(
              classes.optionCard,
              option.disabled
                ? classes.optionCardDisabled
                : classes.optionCardEnabled,
              enrollOption === option.key && classes.optionCardSelected
            )}
            onClick={() => {
              if (option.disabled) return;
              handleOptionChange(option.key);
              optionRadioRefs[option.key].current?.focus();
            }}
          >
            {/* MUI's CardContent:last-child rule bumps padding-bottom to 24px; override to match the other sides. */}
            <CardContent className={classes.cardContent}>
              <Flex className={classes.optionRow}>
                <FormControlLabel
                  value={option.key}
                  disabled={option.disabled}
                  control={
                    <Radio
                      slotProps={{
                        input: { ref: optionRadioRefs[option.key] },
                      }}
                      checked={enrollOption === option.key}
                      className={classes.radio}
                    />
                  }
                  onClick={(e) => e.stopPropagation()}
                  label={
                    <TranslatedLabel
                      translationKey={option.labelKey}
                      variables={{
                        count: option.count,
                        // i18next context: `<key>_selected` carries the count.
                        context:
                          enrollOption === option.key ? 'selected' : undefined,
                      }}
                      typographyVariant="body_medium"
                    />
                  }
                />
                {option.showButton && (
                  <Button
                    variant="button-link"
                    disabled={
                      isEligibilityLoading ||
                      isEligibilityError ||
                      eligibleCount === 0
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      option.onButtonClick();
                    }}
                  >
                    {t(option.buttonLabelKey)}
                  </Button>
                )}
              </Flex>
            </CardContent>
          </Card>
        ))}
      </CatalogRadioGroup>
    </WizardSection>
  );
}

export default VehicleSelectionStep;
